import { useEffect, useState } from "react";
import { Activity, Undo2, Loader2, Mail, MailX, UserCheck, Tag, Bell, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const ICONS = { assign: UserCheck, status: Tag, reminder: Bell, email: Mail, email_skipped: MailX };
const STATUS = { NEW: "New", EDITING: "Editing", READY: "Ready", DELIVERED: "Delivered" };

function describe(c) {
  if (c.kind === "assign") return `assigned to ${c.to.editor_name}${c.from.editor_name ? ` (was ${c.from.editor_name})` : ""}`;
  if (c.kind === "status") return `status ${STATUS[c.from] || c.from} → ${STATUS[c.to] || c.to}`;
  if (c.kind === "reminder") return "added a Smart Reminder";
  if (c.kind === "email_skipped") return `email not sent: ${c.reason}`;
  if (c.subject) return `emailed ${c.to}: “${c.subject}”`;
  return `emailed ${c.to} about ${c.count} product(s)`;
}

const Row = ({ a, busy, onUndo }) => {
  const canUndo = !a.undone && a.changes.some((c) => ["assign", "status", "reminder"].includes(c.kind));
  return (
    <li className={`flex items-start gap-3 py-2.5 ${a.undone ? "opacity-50" : ""}`} data-testid={`activity-row-${a.id}`}>
      <span className="w-14 shrink-0 font-mono text-[11px] text-muted-foreground">{new Date(a.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
      <div className="min-w-0 flex-1 text-xs">
        <div className="font-medium text-foreground">{a.automation_name}</div>
        {a.address && <div className="text-muted-foreground">{a.type} · {a.address}{a.client_name ? ` · ${a.client_name}` : ""}</div>}
        <ul className="mt-1 space-y-0.5">
          {a.changes.map((c, i) => {
            const Icon = ICONS[c.kind];
            return <li key={i} className="flex items-center gap-1.5 text-foreground/80"><Icon className="h-3 w-3 text-primary" />{describe(c)}</li>;
          })}
        </ul>
      </div>
      {a.undone ? <span className="text-[11px] text-muted-foreground" data-testid={`activity-undone-${a.id}`}>Undone</span> : canUndo && (
        <button onClick={() => onUndo(a)} disabled={busy} data-testid={`activity-undo-${a.id}`}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-[11px] font-semibold transition-colors hover:bg-secondary">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Undo
        </button>
      )}
    </li>
  );
};

export const AutomationActivity = ({ refreshKey }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(() => localStorage.getItem("automationActivityOpen") !== "0");
  const toggle = () => setOpen((o) => { localStorage.setItem("automationActivityOpen", o ? "0" : "1"); return !o; });
  const load = () => api.get("/automations/activity").then(({ data }) => setItems(data.activity)).catch(() => setItems([]));
  useEffect(() => { load(); }, [refreshKey]);

  const undo = async (a) => {
    setBusy(a.id);
    try {
      const { data } = await api.post(`/automations/activity/${a.id}/undo`);
      if (data.skipped?.length) toast.warning(`Undone, but someone changed the ${data.skipped.join(" and ")} since, so that part was left alone`);
      else toast.success("Change undone");
      load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(null);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="automation-activity">
      <button onClick={toggle} aria-expanded={open} data-testid="automation-activity-toggle"
        className="flex w-full items-center gap-2 text-left text-sm font-semibold">
        <Activity className="h-4 w-4 text-primary" /> Today's activity
        {items !== null && <span className="text-xs font-normal text-muted-foreground" data-testid="automation-activity-count">({items.length})</span>}
        <ChevronDown className={`ml-auto h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-1" data-testid="automation-activity-body">{items === null ? <Loader2 className="mt-2 h-4 w-4 animate-spin text-muted-foreground" />
        : !items.length ? <p className="text-xs text-muted-foreground" data-testid="automation-activity-empty">Nothing yet today. Changes your automations make will show up here.</p>
        : <ul className="max-h-80 divide-y divide-border overflow-y-auto">{items.map((a) => <Row key={a.id} a={a} busy={busy === a.id} onUndo={undo} />)}</ul>}</div>}
    </div>
  );
};
